import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Users, Bot, Zap, Target, Crown } from 'lucide-react';

const GameModeSelector = ({ onModeSelect, onBack, selectedDeck, deckInfo }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 flex items-center justify-center">
      <div className="max-w-4xl mx-auto text-center">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={onBack}
            size="sm"
            variant="outline"
            className="bg-white/10 border-gray-600 text-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Decks
          </Button>
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <span className="text-2xl">{deckInfo.icon}</span>
            {deckInfo.name} Battle
          </h1>
          <p className="text-gray-300 text-lg">Choose your game mode!</p>
        </div>

        {/* Game Mode Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Human vs Human */}
          <Card className="bg-white/10 backdrop-blur-sm border-gray-600 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">
                <Users className="w-16 h-16 mx-auto text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Human vs Human</h2>
              <p className="text-gray-300 mb-6">Play against a friend on the same device</p>
              <ul className="text-sm text-gray-400 mb-6 space-y-1">
                <li>• Take turns selecting cards</li>
                <li>• Test each other's knowledge</li>
                <li>• Classic two-player experience</li>
              </ul>
              <Button 
                onClick={() => onModeSelect('human_vs_human')}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
              >
                Play vs Friend
              </Button>
            </div>
          </Card>

          {/* Human vs Computer */}
          <Card className="bg-white/10 backdrop-blur-sm border-gray-600 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">
                <Bot className="w-16 h-16 mx-auto text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Human vs Computer</h2>
              <p className="text-gray-300 mb-6">Challenge the AI with different difficulty levels</p>
              
              {/* Difficulty Selection */}
              <div className="space-y-3 mb-6">
                <Button 
                  onClick={() => onModeSelect('human_vs_computer', 'easy')}
                  size="sm"
                  className="w-full bg-green-500 hover:bg-green-600 text-white py-2 text-sm"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Easy AI - Random Play
                </Button>
                <Button 
                  onClick={() => onModeSelect('human_vs_computer', 'medium')}
                  size="sm"
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-2 text-sm"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Medium AI - Smart Strategy
                </Button>
                <Button 
                  onClick={() => onModeSelect('human_vs_computer', 'hard')}
                  size="sm"
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-2 text-sm"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Hard AI - Expert Level
                </Button>
              </div>
            </div>
          </Card>

          {/* Gallery Mode - Full Width */}
          <Card className="bg-white/10 backdrop-blur-sm border-purple-500 md:col-span-2 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 cursor-pointer" onClick={() => onModeSelect('gallery')}>
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="text-5xl">🎨</div>
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-white">Card Gallery</h2>
                  <p className="text-gray-300">View all cards and artwork in this deck</p>
                </div>
              </div>
              <Button 
                onClick={(e) => {
                  e.stopPropagation();
                  onModeSelect('gallery');
                }}
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8"
              >
                View Gallery
              </Button>
            </div>
          </Card>
        </div>

        {/* Game Rules Reminder */}
        <div className="bg-white/5 rounded-lg p-4 text-white">
          <h3 className="text-lg font-semibold mb-2">Game Rules</h3>
          <p className="text-sm text-gray-300">
            5 cards each • First to 3 tricks wins • Lower rank number beats higher rank number
          </p>
        </div>
      </div>
    </div>
  );
};

export default GameModeSelector;